import React, {FC} from 'react';
import {useQuery} from '@tanstack/react-query';
import {format, formatDistanceToNow, parseISO} from 'date-fns';
import {Link as ReactLink} from 'react-router-dom';
import {Glum} from '../../../puddleglum';
import {Badge, Box, Button, Heading, HStack, Link, Text, VStack} from 'components/ui/chakra';
import PatientVoucherController from 'puddleglum/api/patient/PatientVoucherController';

type VoucherLike = Glum.Models.Voucher & {
	code?: string | null;
	voucher_code?: string | null;
};

const getVoucherCode = (voucher: VoucherLike): string =>
	voucher.code || voucher.voucher_code || `Voucher #${voucher.id}`;

const getServiceName = (voucher: VoucherLike): string =>
	voucher.price?.display_name ||
	voucher.price?.treatment?.treatment ||
	voucher.price?.treatment_group?.name ||
	'Service voucher';

const formatExpiry = (expiresAt: string | null): string => {
	if (!expiresAt) return 'No expiration';

	const expires = parseISO(expiresAt);
	if (Number.isNaN(expires.getTime())) return 'No expiration';

	return `${format(expires, 'MMM d, yyyy')} (${formatDistanceToNow(expires, {addSuffix: true})})`;
};

const UnredeemedVouchers: FC = () => {
	const {data: vouchers} = useQuery(['patient-vouchers-summary'], async () => {
		const {data} = await PatientVoucherController.index();
		return data as VoucherLike[];
	});

	if (!vouchers || vouchers.length === 0) return null;

	const displayed = vouchers.slice(0, 3);

	return (
		<Box w='full'>
			<HStack justify='space-between' align='center' mb={3}>
				<Heading size='md' color='gray.500'>
					Unredeemed Vouchers
				</Heading>
				<Link as={ReactLink} to='/vouchers' color='blue.600' fontSize='sm'>
					View all
				</Link>
			</HStack>

			<VStack spacing={3} w='full' align='stretch'>
				{displayed.map((voucher) => (
					<Box
						key={voucher.id}
						w='full'
						p={4}
						bg='white'
						border='1px solid'
						borderColor='gray.200'
						borderRadius='md'
					>
						<HStack justify='space-between' align='flex-start'>
							<Box>
								<Text fontSize='sm' fontWeight='semibold' color='gray.700'>
									{getServiceName(voucher)}
								</Text>
								<Text fontSize='xs' color='gray.500' mt={1}>
									{getVoucherCode(voucher)}
								</Text>
							</Box>
							<Badge colorScheme={voucher.status === 'active' ? 'green' : 'orange'}>
								{voucher.status === 'pending_payment'
									? 'Pending payment'
									: 'Active'}
							</Badge>
						</HStack>
						<Text fontSize='xs' color='gray.500' mt={2}>
							Expires: {formatExpiry(voucher.expires_at ?? null)}
						</Text>
					</Box>
				))}
			</VStack>

			<Button as={ReactLink} to='/vouchers' variant='outline' mt={3} w='full'>
				Open Vouchers
			</Button>
		</Box>
	);
};

export default UnredeemedVouchers;
